import React from "react";
import { Map as MapIcon, ArrowRight, Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { useTenant } from "@/lib/TenantContext";
import { tenantHref } from "@/lib/tenantPath";
// Relative on purpose: pure modules, resolvable under Jest in every suite that mounts the
// portal without a shim.
import Section from "../PortalSection";
import LoadFailure from "../../components/ui/LoadFailure";
import useMemberPathways from "../../pathways/useMemberPathways";
import { CATALOGUE_PATH, pathwayPath, stepPath } from "../../pathways/learnerModel";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";

/**
 * "My pathways", a section of the client portal (`/compte`), read from
 * `GET /client/pathways` (contract §5.4, §9): one Continue card per pathway, newest
 * enrolment first.
 *
 *   in progress   "3 of 8 steps · 37%" and the bar, then "Continue · <current step>",
 *                 straight to that step's screen (the learner never wonders where they were)
 *   no current    (every open step done, the rest locked) "Open the pathway", to its home
 *   completed     "Completed", and "Open the pathway"
 *
 * OWNS ITS READ. The section's failure and loading are stated inside the section: the
 * bookings, courses and messages around it render from their own sources.
 *
 * Unconditional, like the courses section: an empty list is an answer ("you are not in any
 * pathway"). The way to the catalogue is offered with it only while the catalogue has
 * something to join (`has_active_pathways === true`, the gate the nav link uses in
 * lib/siteLinks.js).
 *
 * Mount only under a signed-in client: ClientPortal renders its sections only once `user`
 * is set.
 */
export default function PortalPathways() {
  const t = useT("public");
  const { tenant } = useTenant();
  const { data, loading, error, reload } = useMemberPathways();
  const rows = data || [];

  let body;
  if (loading && !data) {
    body = <Loader2 className="animate-spin text-eh-sage-deep" size={22} aria-hidden="true" />;
  } else if (error) {
    body = (
      <LoadFailure
        message={t("portal.pathways.loadFailed")}
        onRetry={reload}
        retryLabel={t("pathways.retry")}
        testId={IDS.portalLoadError}
      />
    );
  } else if (rows.length === 0) {
    body = (
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid={IDS.portalEmpty}>
        <p className="text-eh-muted">{t("portal.pathways.empty")}</p>
        {tenant?.has_active_pathways === true && (
          <a href={tenantHref(CATALOGUE_PATH)} className="inline-flex items-center gap-2 text-sm font-medium text-eh-ink underline">
            {t("portal.pathways.browse")} <ArrowRight size={15} aria-hidden="true" />
          </a>
        )}
      </div>
    );
  } else {
    body = (
      <ul className="grid gap-4 sm:grid-cols-2">
        {rows.map((row) => <ContinueCard key={row.id} row={row} />)}
      </ul>
    );
  }

  return (
    <Section icon={MapIcon} title={t("portal.pathways")} count={rows.length} testid={IDS.portalSection}>
      {body}
    </Section>
  );
}

function ContinueCard({ row }) {
  const t = useT("public");
  const percent = Math.max(0, Math.min(100, Number(row.percent) || 0));
  const current = row.completed_at ? null : row.current_step;
  const href = current ? stepPath(row.id, current.id) : pathwayPath(row.id);

  return (
    <li data-testid={IDS.portalCard(row.id)} className="flex flex-col rounded-2xl border border-eh-border-soft bg-eh-surface p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="min-w-0 break-words font-medium text-eh-ink">{row.title}</h3>
        {row.completed_at && (
          <span className="rounded-full bg-eh-sage-soft px-2.5 py-0.5 text-xs text-eh-ink">{t("portal.pathways.completed")}</span>
        )}
        {row.status === "archived" && (
          <span className="rounded-full bg-eh-cream-deep px-2.5 py-0.5 text-xs text-eh-muted">{t("portal.pathways.archived")}</span>
        )}
      </div>
      <p className="mt-2 flex items-baseline justify-between text-xs text-eh-muted">
        <span>{t("pathways.home.progress", { done: row.required_completed, total: row.required_total })}</span>
        <span data-testid={IDS.portalPercent(row.id)} className="font-semibold tabular-nums text-eh-ink">{t("pathways.percent", { percent })}</span>
      </p>
      <div
        role="progressbar"
        aria-label={t("pathways.home.progressLabel")}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-eh-cream-deep"
      >
        <div className="h-full rounded-full bg-eh-sage-deep" style={{ width: `${percent}%` }} />
      </div>
      <a
        href={tenantHref(href)}
        data-testid={IDS.portalContinue(row.id)}
        className="mt-4 inline-flex h-11 items-center justify-center gap-2 rounded-full bg-eh-ink px-5 text-sm font-semibold text-eh-cream hover:bg-eh-ink/90"
      >
        <span className="truncate">
          {current ? t("pathways.home.continue", { title: current.title }) : t("portal.pathways.open")}
        </span>
        <ArrowRight size={16} className="flex-shrink-0" aria-hidden="true" />
      </a>
    </li>
  );
}
